import { ART_PALETTE } from '../art-palette.js';
import { MASK, PIPE_MASKS } from './masks.js';
import { blankPixels, plainTile, randomVariantTile } from './helpers.js';

// House style: flat hybrid (docs/art-direction-rules.md §0).
//   * Every discrete plant is fully enclosed by a 1px Ink contour, diagonal
//     corner gaps included, and sits on a flat Dark grass contact shadow.
//   * Three values per material only: lit cap (upper-left), body, dark underside.
//   * Texture is a motif family (blade shapes, leaf-clump notches), never a
//     per-pixel formula.
//   * flora.hedge is connected terrain: seam edges carry no contour, only free
//     ends do, so the tile grid never shows.

const INK = 1;
const SHADOW = 13;
const NEIGHBOURS = [[0, -1], [1, 0], [0, 1], [-1, 0], [1, 1], [1, -1], [-1, 1], [-1, -1]];

// Three-value material ramps: [lit cap, body, dark underside].
const LEAF = [16, 15, 12];   // living foliage
const STRAW = [9, 8, 3];     // cured / dry grass
const BARK = [8, 7, 3];      // sapling trunk, cattail head
const HEDGE = [16, 15, 12];  // clipped hedge wall

/** Minimal 16×16 surface with the same grammar as tools/render-style-reference.js. */
function sheet() {
  const px = blankPixels();
  const inside = (x, y) => x >= 0 && x < 16 && y >= 0 && y < 16;
  const api = {
    px,
    set(x, y, color) { if (inside(x, y)) px[y][x] = color; },
    at(x, y) { return inside(x, y) ? px[y][x] : 0; },
    hline(x, y, width, color) { for (let i = 0; i < width; i++) api.set(x + i, y, color); },
    vline(x, y, height, color) { for (let i = 0; i < height; i++) api.set(x, y + i, color); },
    rect(x, y, width, height, color) { for (let j = 0; j < height; j++) api.hline(x, y + j, width, color); },
    /** Wrap every opaque cluster in `color`, diagonal corner gaps included. */
    outline(color) {
      const copy = px.map((row) => [...row]);
      for (let y = 0; y < 16; y++) {
        for (let x = 0; x < 16; x++) {
          if (copy[y][x]) continue;
          if (NEIGHBOURS.some(([dx, dy]) => copy[y + dy]?.[x + dx])) px[y][x] = color;
        }
      }
    },
  };
  return api;
}

/** Draw, contour in Ink, then drop the result onto its own contact shadow. */
function planted(drawArt, shadowBands = []) {
  const art = sheet();
  drawArt(art);
  art.outline(INK);
  const out = sheet();
  for (const [x, y, width] of shadowBands) out.hline(x, y, width, SHADOW);
  for (let y = 0; y < 16; y++) {
    for (let x = 0; x < 16; x++) if (art.px[y][x]) out.px[y][x] = art.px[y][x];
  }
  return out.px;
}

/** Membership test for a mass described as inclusive [row, x0, x1] spans. */
function mass(spans) {
  const cells = new Set();
  for (const [y, x0, x1] of spans) {
    for (let x = x0; x <= x1; x++) cells.add(y * 16 + x);
  }
  return (x, y) => x >= 0 && x < 16 && y >= 0 && y < 16 && cells.has(y * 16 + x);
}

/**
 * Flat three-value shading that follows the silhouette instead of a straight
 * diagonal. Stepping away from the light (down-right) and falling out of the
 * mass means this pixel is on the shaded rim; still being inside four pixels
 * later means it is deep in the upper-left cap. Same construction as the
 * canopy on the house-style board, scaled to a 16×16 form.
 */
function shadeMass(s, has, [cap, body, dark]) {
  for (let y = 0; y < 16; y++) {
    for (let x = 0; x < 16; x++) {
      if (!has(x, y)) continue;
      if (!has(x + 2, y + 2) || !has(x, y + 2)) s.set(x, y, dark);
      else if (has(x + 4, y + 4)) s.set(x, y, cap);
      else s.set(x, y, body);
    }
  }
}

/**
 * Leaf-clump notches (§0 foliage motif): bite body-coloured clumps out of the
 * lit cap along the terminator. Only the two existing values are used, so the
 * material still spends exactly three steps.
 */
function scallopCap(s, [cap, body]) {
  const edge = [];
  for (let y = 0; y < 16; y++) {
    for (let x = 0; x < 16; x++) {
      if (s.at(x, y) !== cap) continue;
      if (s.at(x + 1, y) === body || s.at(x, y + 1) === body) edge.push([x, y]);
    }
  }
  edge.forEach(([x, y], index) => {
    if (index % 3 !== 1) return;
    s.set(x, y, body);
    if (s.at(x - 1, y) === cap) s.set(x - 1, y, body);
  });
}

// ---------------------------------------------------------------------------
// Shrubs and saplings — rounded flat masses, plain tiles.
// ---------------------------------------------------------------------------

const SHRUB_FORMS = {
  // Plump dome.
  round: {
    spans: [[2, 6, 9], [3, 4, 11], [4, 3, 12], [5, 2, 13], [6, 2, 13],
      [7, 2, 13], [8, 2, 13], [9, 2, 13], [10, 3, 12], [11, 4, 11], [12, 6, 9]],
    shadow: [[2, 13, 13], [4, 14, 10]],
  },
  // Two shoots over a lopsided mound.
  wild: {
    spans: [[1, 3, 5], [2, 2, 7], [3, 2, 10], [4, 2, 12], [5, 2, 13],
      [6, 2, 13], [7, 2, 13], [8, 2, 13], [9, 2, 13],
      [10, 3, 12], [11, 4, 11], [12, 6, 10]],
    shadow: [[2, 13, 13], [4, 14, 10]],
  },
  // Low spreading mound (red berries).
  berry: {
    spans: [[5, 6, 10], [6, 4, 12], [7, 3, 13], [8, 2, 13], [9, 2, 13],
      [10, 3, 12], [11, 4, 11], [12, 6, 10]],
    shadow: [[2, 13, 13], [4, 14, 10]],
  },
  // Same family, longer right shoulder (gold berries).
  berryWide: {
    spans: [[4, 7, 10], [5, 5, 11], [6, 3, 12], [7, 2, 13], [8, 2, 13],
      [9, 2, 13], [10, 3, 13], [11, 5, 11], [12, 7, 10]],
    shadow: [[3, 13, 12], [5, 14, 9]],
  },
  // Taller, narrower crown so it is not another onion.
  flowering: {
    spans: [[2, 6, 9], [3, 5, 10], [4, 4, 11], [5, 3, 12], [6, 3, 12],
      [7, 3, 12], [8, 3, 12], [9, 3, 12], [10, 4, 11], [11, 5, 10], [12, 7, 9]],
    shadow: [[3, 13, 12], [5, 14, 9]],
  },
};

/** Berry clump: 3px of one saturated accent plus a single dark seed pixel. */
function berryClump(s, has, x, y, accent, dark) {
  if (has(x, y)) s.set(x, y, accent);
  if (has(x + 1, y)) s.set(x + 1, y, accent);
  if (has(x, y + 1)) s.set(x, y + 1, accent);
  if (has(x + 1, y + 1)) s.set(x + 1, y + 1, dark);
}

/** Bloom clump on a flowering shrub: 2–3px of one accent, no gradient. */
function bloomClump(s, has, x, y, accent, tall) {
  if (has(x, y)) s.set(x, y, accent);
  if (has(x + 1, y)) s.set(x + 1, y, accent);
  if (tall && has(x, y + 1)) s.set(x, y + 1, accent);
}

function shrubPixels(form, decoration = null) {
  const { spans, shadow } = SHRUB_FORMS[form];
  const has = mass(spans);
  return planted((s) => {
    shadeMass(s, has, LEAF);
    scallopCap(s, LEAF);
    if (decoration?.berries) {
      const [accent, dark] = decoration.berries;
      for (const [x, y] of decoration.spots) berryClump(s, has, x, y, accent, dark);
    }
    if (decoration?.blooms) {
      const accent = decoration.blooms;
      decoration.spots.forEach(([x, y], index) => bloomClump(s, has, x, y, accent, index % 2 === 0));
    }
  }, shadow);
}

function saplingPixels(kind) {
  const windblown = kind === 'windblown';
  const canopy = windblown
    ? mass([[2, 8, 12], [3, 7, 13], [4, 6, 13], [5, 6, 13], [6, 6, 12], [7, 7, 11], [8, 9, 11]])
    : mass([[2, 6, 9], [3, 4, 11], [4, 3, 12], [5, 3, 12], [6, 3, 12], [7, 4, 11], [8, 6, 9]]);
  const trunk = windblown
    ? [[8, 9], [9, 9], [8, 10], [9, 10], [7, 11], [8, 11], [7, 12], [8, 12]]
    : [[7, 8], [8, 8], [7, 9], [8, 9], [7, 10], [8, 10], [7, 11], [8, 11], [7, 12], [8, 12]];
  const flare = [6, 7, 8, 9];
  return planted((s) => {
    for (const [x, y] of trunk) s.set(x, y, x % 2 === 0 ? BARK[2] : BARK[1]);
    for (const x of flare) s.set(x, 13, BARK[2]);
    s.set(flare[0], 13, BARK[1]);
    shadeMass(s, canopy, LEAF);
    scallopCap(s, LEAF);
  }, [[3, 14, 12], [5, 15, 9]]);
}

// ---------------------------------------------------------------------------
// Flowers — the bloom is the focal point. One saturated accent with an Ink
// contour on a Dark grass stem; most of the tile stays empty.
// ---------------------------------------------------------------------------

const STEM = 13;
const STEM_BASE = 11;

/**
 * Stem layouts per bloom family. Heads sit high in the tile on 3–7px stems so a
 * planted flower is as tall as the shrubs it stands next to, and each variant
 * differs in stem count, spacing and head height — not just colour.
 */
const FLOWER_LAYOUTS = {
  // [stemX, headY, openBloom]
  disc: [
    [[4, 5, 1], [10, 8, 1]],
    [[3, 8, 1], [9, 5, 1], [13, 8, 0]],
    [[6, 4, 1], [11, 8, 1], [2, 9, 0]],
    [[4, 8, 1], [10, 5, 1], [14, 9, 0]],
  ],
  sparse: [
    [[7, 4, 1]],
    [[5, 5, 1], [11, 9, 0]],
    [[9, 4, 1], [4, 9, 0]],
    [[5, 6, 1], [11, 4, 1]],
  ],
  tiny: [
    [[3, 8, 1], [7, 6, 1], [11, 9, 1]],
    [[4, 9, 1], [9, 7, 1], [13, 8, 0]],
    [[2, 8, 1], [6, 6, 1], [10, 9, 1], [13, 7, 0]],
    [[5, 6, 1], [10, 8, 1]],
  ],
  // [stemX, spikeTopY, spikeHeight]
  spike: [
    [[5, 4, 5], [11, 6, 4]],
    [[4, 6, 4], [9, 3, 6], [13, 7, 3]],
    [[7, 3, 6], [12, 6, 4]],
    [[3, 6, 4], [8, 4, 5], [12, 5, 5]],
  ],
};

// Petals get their own lit / body / centre so a bloom is a small flat volume
// rather than one flat dot. Every set is chosen to hold up against Grass (14):
// the cool species borrow a light water step so they stop reading as dark specks.
const FLOWER_SPECIES = {
  daisy: { head: 'disc', layout: 'disc', lit: 23, body: 11, center: 9 },
  marigold: { head: 'disc', layout: 'disc', lit: 10, body: 9, center: 7 },
  pinkWildflower: { head: 'disc', layout: 'disc', lit: 30, body: 30, center: 11 },
  blueWildflower: { head: 'disc', layout: 'disc', lit: 21, body: 29, center: 22 },
  starflower: { head: 'star', layout: 'sparse', lit: 23, body: 11, center: 30 },
  moonflower: { head: 'star', layout: 'sparse', lit: 23, body: 22, center: 29 },
  clover: { head: 'tiny', layout: 'tiny', lit: 11, body: 30, center: 11 },
  bluebells: { head: 'bell', layout: 'disc', lit: 21, body: 29, center: 22 },
  foxglove: { head: 'spike', layout: 'spike', lit: 30, body: 30, center: 11 },
  lavender: { head: 'spike', layout: 'spike', lit: 30, body: 29, center: 11 },
  fireweed: { head: 'spike', layout: 'spike', lit: 28, body: 28, center: 11 },
  meadowMix: {
    head: 'disc',
    layout: 'disc',
    lit: 28,
    body: 28,
    center: 11,
    mix: [[28, 28, 11], [11, 11, 28], [30, 30, 11]],
  },
};

/** Open bloom: a 5×3 rounded head — the largest mass on the sprite. */
function drawDisc(s, x, y, lit, body, center) {
  s.hline(x - 1, y, 3, lit);
  s.hline(x - 2, y + 1, 5, body);
  s.set(x - 1, y + 1, lit);
  s.set(x, y + 1, center);
  s.hline(x - 1, y + 2, 3, body);
}

/** Star bloom: a 5×5 cross so the silhouette differs from the disc family. */
function drawStar(s, x, y, lit, body, center) {
  s.set(x, y, lit);
  s.hline(x - 1, y + 1, 3, lit);
  s.hline(x - 2, y + 2, 5, body);
  s.set(x - 1, y + 2, lit);
  s.set(x, y + 2, center);
  s.hline(x - 1, y + 3, 3, body);
  s.set(x, y + 4, body);
}

/** Tiny bloom for clover-scale plants. */
function drawTiny(s, x, y, lit, body, center) {
  s.hline(x - 1, y, 3, lit);
  s.hline(x - 1, y + 1, 3, body);
  s.set(x, y + 1, center);
}

/** Closed bud: the small companion shape in every layout. */
function drawBud(s, x, y, lit, body, center) {
  s.set(x, y, lit);
  s.set(x + 1, y, lit);
  s.set(x, y + 1, body);
  s.set(x + 1, y + 1, center);
}

/** Bell cluster: a capped 3×3 head with two hanging tips. */
function drawBell(s, x, y, lit, body, center) {
  s.hline(x - 1, y, 3, lit);
  s.hline(x - 1, y + 1, 3, body);
  s.set(x, y + 1, center);
  s.set(x - 1, y + 2, body);
  s.set(x + 1, y + 2, body);
}

/** Floret spike: a body core with alternating lit florets on each side. */
function drawSpike(s, x, top, height, lit, body, center) {
  for (let i = 0; i < height; i++) {
    const y = top + i;
    s.set(x, y, i === 0 ? lit : body);
    if (i === 0) continue;
    s.set(x + (i % 2 ? 1 : -1), y, i % 3 === 0 ? center : lit);
    if (i > 1 && i < height - 1) s.set(x + (i % 2 ? -1 : 1), y, body);
  }
}

function flowerPixels(species, variant, sway = 0) {
  const spec = FLOWER_SPECIES[species];
  const layout = FLOWER_LAYOUTS[spec.layout][variant % 4];
  const shadow = [];
  const xs = layout.map((entry) => entry[0]);
  const left = Math.max(0, Math.min(...xs) - 1);
  const right = Math.min(15, Math.max(...xs) + 1);

  const pixels = planted((s) => {
    // Shared leaf base drawn first. Every stem lands in it, so the clump takes
    // one Ink perimeter instead of one per bloom — the trick the style board's
    // flower prop uses, and the reason the accents stay the loudest thing here.
    // It humps under each stem and dips between them, so it reads as low
    // foliage rather than a planter rim.
    for (let x = left; x <= right; x++) {
      const near = Math.min(...xs.map((sx) => Math.abs(sx - x)));
      const bottom = (x === left || x === right) ? 12 : 13;
      const top = Math.min(bottom, near === 0 ? 11 : near === 1 ? 12 : 13);
      for (let y = top; y <= bottom; y++) s.set(x, y, y === bottom ? LEAF[2] : LEAF[1]);
    }
    shadow.push([left - 1, 14, right - left + 4], [left, 15, right - left + 2]);

    layout.forEach((entry, index) => {
      const stemX = entry[0];
      const [lit, body, center] = spec.mix
        ? spec.mix[index % spec.mix.length]
        : [spec.lit, spec.body, spec.center];

      if (spec.head === 'spike') {
        const [, top, height] = entry;
        for (let y = top + height; y <= STEM_BASE; y++) {
          s.set(stemX + (y < top + height + 2 ? sway : 0), y, STEM);
        }
        drawSpike(s, stemX + sway, top, height, lit, body, center);
      } else {
        const headY = entry[1];
        const open = entry[2];
        const headBottom = !open ? headY + 1 : spec.head === 'star' ? headY + 4
          : spec.head === 'tiny' ? headY + 1 : headY + 2;
        for (let y = headBottom + 1; y <= STEM_BASE; y++) {
          s.set(stemX + (y < headBottom + 4 ? sway : 0), y, STEM);
        }
        const hx = stemX + sway;
        if (!open) drawBud(s, hx, headY, lit, body, center);
        else if (spec.head === 'star') drawStar(s, hx, headY, lit, body, center);
        else if (spec.head === 'tiny') drawTiny(s, hx, headY, lit, body, center);
        else if (spec.head === 'bell') drawBell(s, hx, headY, lit, body, center);
        else drawDisc(s, hx, headY, lit, body, center);
      }
    });
  }, shadow);
  return pixels;
}

// ---------------------------------------------------------------------------
// Grasses — a blade motif family of four silhouettes, rephrased per species.
// ---------------------------------------------------------------------------

/**
 * Blade motif family, four silhouettes, expressed two ways:
 *  - MOUND profiles are per-column heights for a solid clump with a serrated
 *    top. Short grass has to be solid, or the Ink contour fills the gaps
 *    between one-pixel strands and the tuft reads as a black crown.
 *  - STRAND sets are [offsetX, height, tip drift] for species tall enough that
 *    separate blades still read at 1×.
 */
const MOUND_PROFILES = {
  fan: [2, 5, 1, 6, 3],
  spray: [3, 1, 5, 2, 6, 1, 3],
  pair: [5, 1, 4, 2],
  arc: [2, 4, 1, 3],
};

const STRAND_BLADES = {
  fan: [[-1, 4, -2], [0, 6, 0], [1, 5, 2]],
  spray: [[-2, 3, -2], [-1, 5, -1], [1, 6, 1], [2, 4, 2]],
  pair: [[0, 5, -1], [1, 3, 2]],
  arc: [[-1, 3, -3], [0, 4, -1], [1, 3, 2]],
};

const scaleBlades = (name, factor, curve) => STRAND_BLADES[name]
  .map(([dx, height, drift]) => [dx, Math.max(2, Math.round(height * factor)), drift * curve]);

/** Solid clump whose top edge carries the blade shapes. */
function drawMound(s, baseX, baseY, name, factor, ramp, sway) {
  const [cap, body, dark] = ramp;
  MOUND_PROFILES[name].forEach((raw, column) => {
    const height = Math.max(1, Math.round(raw * factor));
    for (let k = 0; k <= height; k++) {
      const x = baseX + column + (k > height * 0.55 ? sway : 0);
      s.set(x, baseY - k, k >= height - 1 ? cap : k <= 1 ? dark : body);
    }
  });
}

const moundWidth = (name) => MOUND_PROFILES[name].length;

/** Separate blades for tall species; each strand keeps its own tapered value. */
function drawStrands(s, cx, baseY, blades, ramp, sway) {
  const [cap, body, dark] = ramp;
  for (const [dx, height, drift] of blades) {
    for (let i = 0; i <= height; i++) {
      const t = i / height;
      const bend = drift * t * t + (t > 0.45 ? sway : 0);
      const x = Math.round(cx + dx + bend);
      const y = baseY - i;
      s.set(x, y, i >= height - 1 ? cap : t > 0.35 ? body : dark);
    }
  }
  s.hline(cx - 1, baseY, 3, dark);
}

/** Cattail: straight stem, brown sausage head, two base leaves. */
function drawCattail(s, x, headTop, sway) {
  const stemTop = headTop + 4;
  for (let y = stemTop; y <= 12; y++) s.set(x + (y < stemTop + 3 ? sway : 0), y, STEM);
  const hx = x + sway;
  for (let i = 0; i < 4; i++) {
    s.set(hx, headTop + i, i === 3 ? BARK[2] : i < 2 ? BARK[0] : BARK[1]);
    s.set(hx + 1, headTop + i, i === 3 ? BARK[2] : BARK[1]);
  }
  s.set(hx, headTop - 1, STEM);
}

/** Fern frond: a lanceolate blade with serrated edges and a bare stipe. */
function drawFrond(s, bx, baseY, height, lean, sway) {
  const [cap, body, dark] = LEAF;
  for (let i = 0; i < height; i++) {
    const t = i / (height - 1);
    const x = Math.round(bx + lean * t * t + (t > 0.5 ? sway : 0));
    const y = baseY - i;
    const taper = Math.max(0, Math.min(1, (t - 0.25) / 0.75));
    const half = Math.round(2.1 * Math.sin(Math.PI * taper));
    if (half <= 0) { s.set(x, y, i === height - 1 ? cap : dark); continue; }
    for (let dx = -half; dx <= half; dx++) {
      if (half > 1 && dx === (i % 2 === 0 ? half : -half)) continue;
      s.set(x + dx, y, dx < 0 ? cap : dx === 0 ? body : dark);
    }
  }
}

/** Creeping pad: a rounded 5×3 leaf on a short runner, three flat values. */
function drawCreepLeaf(s, x, y, sway) {
  const [cap, body, dark] = LEAF;
  s.hline(x + 1, y, 3, cap);
  s.hline(x, y + 1, 5, body);
  s.hline(x, y + 1, 2, cap);
  s.hline(x + 1, y + 2, 3, dark);
  s.set(x + 2 + sway, y + 3, dark);
}

const GRASS_PLANS = {
  // mound: [leftX, baseY, profile]   strand: [centreX, baseY, bladeSet]
  tuft: {
    style: 'mound', ramp: LEAF, factor: 1.2,
    variants: [
      [[2, 12, 'fan'], [10, 12, 'pair']],
      [[5, 12, 'spray']],
      [[2, 12, 'arc'], [9, 12, 'fan']],
      [[3, 12, 'pair'], [9, 12, 'arc']],
    ],
  },
  soft: {
    style: 'mound', ramp: LEAF, factor: 0.85,
    variants: [
      [[2, 13, 'arc'], [8, 12, 'spray']],
      [[4, 13, 'spray'], [12, 12, 'pair']],
      [[2, 12, 'pair'], [7, 13, 'arc'], [11, 12, 'pair']],
      [[3, 12, 'fan'], [10, 13, 'arc']],
    ],
  },
  tall: {
    style: 'strand', ramp: LEAF, factor: 1.9, curve: 1.2,
    variants: [
      [[6, 13, 'fan']],
      [[4, 13, 'spray'], [11, 12, 'pair']],
      [[9, 13, 'spray']],
      [[4, 12, 'pair'], [10, 13, 'fan']],
    ],
  },
  dry: {
    style: 'mound', ramp: STRAW, factor: 1.35,
    variants: [
      [[2, 13, 'fan'], [10, 13, 'pair']],
      [[4, 13, 'spray']],
      [[2, 13, 'pair'], [8, 13, 'fan']],
      [[3, 13, 'arc'], [9, 13, 'spray']],
    ],
  },
  reeds: {
    style: 'strand', ramp: [17, 15, 13], factor: 2.1, curve: 0.15,
    variants: [
      [[5, 13, 'pair'], [11, 13, 'fan']],
      [[7, 13, 'fan']],
      [[4, 13, 'fan'], [11, 13, 'pair']],
      [[8, 13, 'spray']],
    ],
  },
};

const CATTAIL_VARIANTS = [
  [[5, 3], [10, 5]],
  [[4, 5], [9, 2], [13, 7]],
  [[7, 3], [12, 6]],
  [[3, 6], [8, 4]],
];

const CREEP_VARIANTS = [
  [[2, 8], [8, 10]],
  [[1, 10], [6, 7], [10, 10]],
  [[3, 7], [9, 9]],
  [[1, 9], [5, 11], [9, 7]],
];

const FERN_VARIANTS = [
  [[5, 9, -2], [8, 11, 0], [11, 9, 2]],
  [[4, 8, -3], [8, 10, 1], [12, 7, 2]],
  [[6, 11, -1], [10, 9, 3]],
  [[3, 8, -2], [7, 10, 0], [11, 8, 3]],
];

function grassPixels(kind, variant, sway = 0) {
  const v = variant % 4;

  if (kind === 'cattails') {
    const stalks = CATTAIL_VARIANTS[v];
    const shadow = [];
    return planted((s) => {
      for (const [x, headTop] of stalks) {
        drawCattail(s, x, headTop, sway);
        shadow.push([x - 3, 13, 7], [x - 2, 14, 5]);
      }
      // A low leaf clump anchors the stalks to the ground.
      drawMound(s, stalks[0][0] - 1, 12, 'arc', 0.7, LEAF, sway);
    }, shadow);
  }

  if (kind === 'groundcover') {
    const leaves = CREEP_VARIANTS[v];
    const shadow = [];
    return planted((s) => {
      for (const [x, y] of leaves) {
        drawCreepLeaf(s, x, y, sway);
        shadow.push([x, y + 4, 5], [x + 1, y + 5, 3]);
      }
    }, shadow);
  }

  if (kind === 'fern') {
    const fronds = FERN_VARIANTS[v];
    const shadow = [];
    return planted((s) => {
      for (const [bx, height, lean] of fronds) {
        drawFrond(s, bx, 12, height, lean, sway);
        shadow.push([bx - 3, 13, 8], [bx - 2, 14, 6]);
      }
      // Shared crown so the fronds read as one plant, not three sticks.
      const xs = fronds.map(([bx]) => bx);
      s.hline(Math.min(...xs), 12, Math.max(...xs) - Math.min(...xs) + 1, LEAF[2]);
    }, shadow);
  }

  const plan = GRASS_PLANS[kind] ?? GRASS_PLANS.tuft;
  const clumps = plan.variants[v];
  const shadow = [];
  return planted((s) => {
    for (const [cx, baseY, set] of clumps) {
      if (plan.style === 'mound') {
        drawMound(s, cx, baseY, set, plan.factor, plan.ramp, sway);
        shadow.push([cx - 1, baseY + 1, moundWidth(set) + 3], [cx, baseY + 2, moundWidth(set) + 1]);
      } else {
        drawStrands(s, cx, baseY, scaleBlades(set, plan.factor, plan.curve), plan.ramp, sway);
        shadow.push([cx - 3, baseY + 1, 8], [cx - 2, baseY + 2, 6]);
      }
    }
  }, shadow);
}

// ---------------------------------------------------------------------------
// Hedge (pipe4). Connected terrain: shading is a pure function of the extended
// occupancy, so both sides of a legal seam compute the identical cross-section
// and no contour is ever drawn on a joined edge. Only free ends get Ink.
// ---------------------------------------------------------------------------

function hedgeVariants() {
  return Object.fromEntries(PIPE_MASKS.map((mask) => {
    const hasN = Boolean(mask & MASK.N);
    const hasE = Boolean(mask & MASK.E);
    const hasS = Boolean(mask & MASK.S);
    const hasW = Boolean(mask & MASK.W);

    // Defined for every integer coordinate, inside the tile or not, so a tile
    // and its neighbour agree about what sits just across the boundary.
    const occupied = (x, y) => {
      const dx = Math.abs(x - 8);
      const dy = Math.abs(y - 8);
      if (dx <= 4 && dy <= 4 && dx + dy <= 7) return true;   // core block, corners clipped
      if (hasN && dx <= 4 && y < 8) return true;
      if (hasS && dx <= 4 && y > 8) return true;
      if (hasW && dy <= 4 && x < 8) return true;
      if (hasE && dy <= 4 && x > 8) return true;
      return false;
    };

    const [cap, body, dark] = HEDGE;
    const s = sheet();
    for (let y = 0; y < 16; y++) {
      for (let x = 0; x < 16; x++) {
        if (!occupied(x, y)) continue;
        const under = !occupied(x, y + 2) || !occupied(x + 2, y);
        const lit = !occupied(x, y - 2) || !occupied(x - 2, y);
        s.set(x, y, under ? dark : lit ? cap : body);
      }
    }

    // Leaf-clump notches: body-coloured bites out of the lit cap, interior only
    // so the 3px seam bands stay byte-identical between neighbours. One ramp
    // step, so a straight run does not flash a repeating pattern at 1×.
    const bites = [];
    for (let y = 4; y <= 11; y++) {
      for (let x = 4; x <= 11; x++) {
        if (s.at(x, y) !== cap) continue;
        if (s.at(x + 1, y) === body || s.at(x, y + 1) === body) bites.push([x, y]);
      }
    }
    bites.forEach(([x, y], index) => { if (index % 4 === 2) s.set(x, y, body); });

    // Contour: Ink only where the hedge genuinely ends. Joined edges continue
    // into the neighbour, so `occupied` reports solid there and no Ink lands.
    const pixels = blankPixels();
    for (let y = 0; y < 16; y++) {
      for (let x = 0; x < 16; x++) {
        if (occupied(x, y)) { pixels[y][x] = s.px[y][x]; continue; }
        if (NEIGHBOURS.some(([dx, dy]) => occupied(x + dx, y + dy))) pixels[y][x] = INK;
      }
    }

    // Contact shadow under a free south end, interior band only so the depth-3
    // east/west seam columns stay byte-identical between neighbours.
    if (!hasS) {
      for (let x = 3; x <= 12; x++) {
        if (!pixels[13][x]) pixels[13][x] = SHADOW;
        if (x >= 4 && x <= 11 && !pixels[14][x]) pixels[14][x] = SHADOW;
      }
    }

    return [mask, pixels];
  }));
}

const SHRUBS_AND_SAPLINGS = [
  ['round-shrub', 'Round shrub', shrubPixels('round')],
  ['wild-shrub', 'Wild shrub', shrubPixels('wild')],
  ['red-berry-bush', 'Red berry bush', shrubPixels('berry', {
    berries: [28, 4], spots: [[4, 8], [9, 6], [6, 11]],
  })],
  ['gold-berry-bush', 'Golden berry bush', shrubPixels('berryWide', {
    berries: [9, 7], spots: [[5, 9], [10, 7], [7, 5]],
  })],
  ['white-flowering-shrub', 'White flowering shrub', shrubPixels('flowering', {
    blooms: 11, spots: [[5, 6], [9, 4], [4, 9], [10, 8], [7, 11]],
  })],
  ['pink-flowering-shrub', 'Pink flowering shrub', shrubPixels('flowering', {
    blooms: 30, spots: [[4, 5], [8, 3], [10, 6], [5, 10], [9, 9]],
  })],
  ['broadleaf-sapling', 'Broadleaf sapling', saplingPixels('broadleaf')],
  ['windblown-sapling', 'Windblown sapling', saplingPixels('windblown')],
].map(([key, name, pixels]) => plainTile({
  key: `flora.${key}`, name, channel: 'onGround', section: 'floraShrubs',
  component: 'onGround', pixels, collision: { ground: true },
}));

const FLOWERS = [
  ['daisy', 'Daisies', 'daisy'],
  ['marigold', 'Marigolds', 'marigold'],
  ['pink-wildflower', 'Pink wildflowers', 'pinkWildflower'],
  ['blue-wildflower', 'Blue wildflowers', 'blueWildflower'],
  ['starflower', 'Starflowers', 'starflower'],
  ['moonflower', 'Moonflowers', 'moonflower'],
  ['tiny-clover-bloom', 'Tiny clover blooms', 'clover'],
  ['bluebells', 'Bluebells', 'bluebells'],
  ['foxglove', 'Foxglove', 'foxglove'],
  ['lavender', 'Lavender', 'lavender'],
  ['fireweed', 'Fireweed', 'fireweed'],
  ['meadow-mix', 'Meadow flower mix', 'meadowMix'],
].map(([key, name, species]) => {
  const variants = Array.from({ length: 4 }, (_, variant) => flowerPixels(species, variant));
  const frames = [-1, 0, 1, 0].map((sway) =>
    Array.from({ length: 4 }, (_, variant) => flowerPixels(species, variant, sway)));
  return randomVariantTile({
    key: `flora.${key}`, name, channel: 'onGround', section: 'floraFlowers',
    component: 'onGround', variants, animationFrames: frames, frameDurationMs: 620,
  });
});

const GRASSES = [
  ['short-grass', 'Short grass tuft', 'tuft'],
  ['soft-grass', 'Soft grass tuft', 'soft'],
  ['tall-grass', 'Tall grass', 'tall'],
  ['dry-grass', 'Dry grass', 'dry'],
  ['river-reeds', 'River reeds', 'reeds'],
  ['cattails', 'Cattails', 'cattails'],
  ['creeping-groundcover', 'Creeping groundcover', 'groundcover'],
  ['fern-patch', 'Fern patch', 'fern'],
].map(([key, name, kind]) => {
  const variants = Array.from({ length: 4 }, (_, variant) => grassPixels(kind, variant));
  const frames = [-1, 0, 1, 0].map((sway) =>
    Array.from({ length: 4 }, (_, variant) => grassPixels(kind, variant, sway)));
  return randomVariantTile({
    key: `flora.${key}`, name, channel: 'onGround', section: 'floraGrasses',
    component: 'onGround', variants, animationFrames: frames,
    frameDurationMs: kind === 'tall' || kind === 'dry' ? 420 : 540,
  });
});

const HEDGE_ART = {
  key: 'flora.hedge', name: 'Maze hedge', channel: 'onGround', mode: 'pipe4', connectionGroup: 'hedge',
  paletteSection: 'floraHedges', palette: ART_PALETTE, collision: { ground: true },
  components: { onGround: hedgeVariants() },
};

export { SHRUBS_AND_SAPLINGS, FLOWERS, GRASSES, HEDGE_ART };
